import { store } from '../app/store';
import { t } from './i18n';
import { AudioPlayer } from '../audio/player';
import { CameraRig, CameraView } from '../engine/cameraRig';
import { EnvironmentSystem } from '../engine/environment';
import { WeatherSystem } from '../engine/weather';
import { showToast } from './toast';
import { openSettingsModal } from './settingsModal';
import { openShortcutsModal } from './shortcutsModal';
import { SCENE_DEFINITIONS } from '../config/scenes';

export class HudUI {
  private cameraRig: CameraRig;
  private env: EnvironmentSystem;
  private weather: WeatherSystem;
  private audioPlayer: AudioPlayer;
  private currentViews: CameraView[] = [];

  constructor(
    cameraRig: CameraRig,
    env: EnvironmentSystem,
    weather: WeatherSystem,
    audioPlayer: AudioPlayer
  ) {
    this.cameraRig = cameraRig;
    this.env = env;
    this.weather = weather;
    this.audioPlayer = audioPlayer;

    this.bindDomEvents();
    this.subscribeToStore();
  }

  public setViews(views: CameraView[]): void {
    this.currentViews = views;
    this.renderViewPills();
  }

  private renderViewPills(): void {
    const viewsBar = document.getElementById('hud-views-bar');
    if (!viewsBar) return;

    viewsBar.innerHTML = '';
    const state = store.getState();

    this.currentViews.forEach((v) => {
      const pill = document.createElement('button');
      pill.className = `view-pill ${state.cameraView === v.id ? 'active' : ''}`;
      pill.innerText = `${v.id}. ${t(v.nameKey, state.language)}`;
      pill.title = `Phím ${v.id}`;
      pill.addEventListener('click', () => {
        store.setState({ cameraView: v.id });
        this.cameraRig.setView(v);
        this.renderViewPills();
        this.updateJoystickVisibility(v);
      });
      viewsBar.appendChild(pill);
    });
  }

  private updateJoystickVisibility(v: CameraView): void {
    const joyContainer = document.getElementById('virtual-joystick-container');
    if (!joyContainer) return;

    // Show joystick on mobile/touch when in fly or boat view
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouch && (v.mode === 'fly' || v.mode === 'boat')) {
      joyContainer.classList.remove('hidden');
    } else {
      joyContainer.classList.add('hidden');
    }
  }

  private bindDomEvents(): void {
    // Scene name
    const hudSceneName = document.getElementById('hud-scene-name');
    hudSceneName?.addEventListener('click', () => {
      this.cycleScene();
    });

    // Scene picker
    document.getElementById('btn-scene-picker')?.addEventListener('click', () => {
      this.cycleScene();
    });

    // Time cycle (T)
    document.getElementById('btn-time-cycle')?.addEventListener('click', () => {
      this.cycleTimeOfDay();
    });

    // Weather cycle
    document.getElementById('btn-weather-cycle')?.addEventListener('click', () => {
      this.cycleWeather();
    });

    // Settings
    document.getElementById('btn-settings')?.addEventListener('click', () => {
      openSettingsModal(this.audioPlayer);
    });

    // Shortcuts (?)
    document.getElementById('btn-shortcuts')?.addEventListener('click', () => {
      openShortcutsModal();
    });

    // HUD toggle (H)
    document.getElementById('btn-hud-toggle')?.addEventListener('click', () => {
      this.toggleHud();
    });

    // Chill mode
    const btnChill = document.getElementById('btn-chill-mode');
    btnChill?.addEventListener('click', () => {
      const isChill = !this.cameraRig.isChillMode;
      this.cameraRig.isChillMode = isChill;
      btnChill.classList.toggle('active', isChill);
      showToast(isChill ? 'Chế độ thong thả (Auto-tour) bật' : 'Chế độ thong thả tắt');
    });

    this.cameraRig.onUserInteracted = () => {
      btnChill?.classList.remove('active');
    };

    // Mini-player buttons
    document.getElementById('btn-audio-play')?.addEventListener('click', () => {
      this.audioPlayer.togglePlay();
    });

    document.getElementById('btn-audio-next')?.addEventListener('click', () => {
      this.audioPlayer.playNext();
    });

    document.getElementById('btn-audio-prev')?.addEventListener('click', () => {
      this.audioPlayer.playPrev();
    });

    document.getElementById('btn-audio-mute')?.addEventListener('click', () => {
      const isMuted = !store.getState().audioMuted;
      this.audioPlayer.setMute(isMuted);
    });

    // Setup virtual joystick for touch
    this.setupVirtualJoystick();
  }

  private setupVirtualJoystick(): void {
    const base = document.getElementById('joystick-base');
    const thumb = document.getElementById('joystick-thumb');
    if (!base || !thumb) return;

    let activeTouchId: number | null = null;
    let baseRect: DOMRect;

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      if (activeTouchId !== null) return;
      const touch = e.changedTouches[0];
      activeTouchId = touch.identifier;
      baseRect = base.getBoundingClientRect();
      handleJoystickMove(touch.clientX, touch.clientY);
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === activeTouchId) {
          handleJoystickMove(e.changedTouches[i].clientX, e.changedTouches[i].clientY);
          break;
        }
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === activeTouchId) {
          activeTouchId = null;
          thumb.style.transform = 'translate(-50%, -50%)';
          this.cameraRig.flyMoveVector.set(0, 0, 0);
          this.cameraRig.boatMoveVector.set(0, 0);
          break;
        }
      }
    };

    const handleJoystickMove = (clientX: number, clientY: number) => {
      const centerX = baseRect.left + baseRect.width / 2;
      const centerY = baseRect.top + baseRect.height / 2;
      const dx = clientX - centerX;
      const dy = clientY - centerY;
      const radius = baseRect.width / 2;
      const dist = Math.hypot(dx, dy);
      const angle = Math.atan2(dy, dx);

      const clampedDist = Math.min(dist, radius);
      const thumbX = Math.cos(angle) * clampedDist;
      const thumbY = Math.sin(angle) * clampedDist;

      thumb.style.transform = `translate(calc(-50% + ${thumbX}px), calc(-50% + ${thumbY}px))`;

      const normX = thumbX / radius;
      const normY = thumbY / radius;

      if (this.cameraRig.currentMode === 'fly') {
        this.cameraRig.flyMoveVector.set(normX, 0, normY);
      } else if (this.cameraRig.currentMode === 'boat') {
        this.cameraRig.boatMoveVector.set(normX, -normY);
      }
    };

    base.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchEnd);
  }

  public toggleHud(): void {
    const top = document.getElementById('hud-top');
    const btm = document.getElementById('hud-bottom');
    const isHidden = top?.classList.toggle('hud-hidden');
    btm?.classList.toggle('hud-hidden');
    store.setState({ hudVisible: !isHidden });
    showToast(isHidden ? 'Đã ẩn giao diện (Nhấn H để hiện)' : 'Đã hiện giao diện');
  }

  public cycleTimeOfDay(step: number = 1): void {
    const current = store.getState().timeOfDay;
    const next = ((current + step) % 6 + 6) % 6;
    store.setState({ timeOfDay: next });
    this.env.setTimePreset(next);
    const lang = store.getState().language;
    const names = [
      t('time_dawn', lang),
      t('time_morning', lang),
      t('time_noon', lang),
      t('time_golden', lang),
      t('time_dusk', lang),
      t('time_night', lang),
    ];
    showToast(`${names[next]}`);
  }

  public cycleWeather(): void {
    const weathers: ('sunny' | 'rain' | 'snow')[] = ['sunny', 'rain', 'snow'];
    const current = store.getState().weather;
    const nextIdx = (weathers.indexOf(current) + 1) % weathers.length;
    const next = weathers[nextIdx];

    // Check scene restrictions
    const sceneDef = SCENE_DEFINITIONS.find(s => s.id === store.getState().sceneId);
    if (sceneDef && !sceneDef.supportedWeather.includes(next)) {
      showToast(t('weather_restricted', store.getState().language));
      return;
    }

    store.setState({ weather: next });
    this.weather.setWeather(next);
    showToast(`${t('weather_' + next, store.getState().language)}`);
  }

  public cycleScene(): void {
    const currentScene = store.getState().sceneId;
    const ids = SCENE_DEFINITIONS.map(s => s.id);
    const nextIdx = (ids.indexOf(currentScene) + 1) % ids.length;
    const nextId = ids[nextIdx];
    window.location.hash = `#scene=${nextId}`;
  }

  private subscribeToStore(): void {
    store.subscribe((state) => {
      // Scene name
      const sceneBadge = document.getElementById('hud-scene-name');
      if (sceneBadge) {
        sceneBadge.innerText = t(`scene_${state.sceneId}`, state.language);
      }

      // Track info
      const trackTitle = document.getElementById('audio-track-title');
      if (trackTitle) {
        trackTitle.innerText = state.currentTrackTitle;
      }
      const trackMood = document.getElementById('audio-track-mood');
      if (trackMood) {
        trackMood.innerText = state.currentTrackMood;
      }

      // Play/Pause icon
      const iconPlay = document.getElementById('icon-play');
      const iconPause = document.getElementById('icon-pause');
      if (state.audioPlaying) {
        iconPlay?.classList.add('hidden');
        iconPause?.classList.remove('hidden');
      } else {
        iconPlay?.classList.remove('hidden');
        iconPause?.classList.add('hidden');
      }

      // Mute icon
      const iconVol = document.getElementById('icon-vol');
      const iconMute = document.getElementById('icon-mute');
      if (state.audioMuted) {
        iconVol?.classList.add('hidden');
        iconMute?.classList.remove('hidden');
      } else {
        iconVol?.classList.remove('hidden');
        iconMute?.classList.add('hidden');
      }

      // Motion
      this.cameraRig.reduceMotion = state.reduceMotion;
      this.weather.setReduceMotion(state.reduceMotion);

      // Environment & Weather sync
      this.env.setTimePreset(state.timeOfDay);
      this.weather.setWeather(state.weather);
      this.audioPlayer.generative.setMood(state.timeOfDay, state.weather);
    });
  }
}
